import type { SkinRarity, WeaponSkinDefinition } from './weapon-skin-types';

/**
 * A weapon case that can be opened with a key.
 */
export interface WeaponCase {
  /** Unique case ID */
  id: number;
  /** Display name */
  name: string;
  /** Description text */
  description: string;
  /** In-game currency cost to open (key price) */
  keyPrice: number;
  /** Skins available in this case, grouped by rarity */
  dropPool: CaseDropPool;
  /** Rare special items (knives/gloves) */
  specialPool: CaseDropEntry[];
  /** Chance of rolling a special item (e.g. 0.0026 = 0.26%) */
  specialRate: number;
  /** Whether this case is currently available */
  active: boolean;
  /** Collection name this case belongs to */
  collection: string;
}

/**
 * Drop pool organized by rarity tier.
 * Each tier has a fixed probability and contains weighted entries.
 */
export interface CaseDropPool {
  consumer: CaseDropEntry[];
  industrial: CaseDropEntry[];
  'mil-spec': CaseDropEntry[];
  restricted: CaseDropEntry[];
  classified: CaseDropEntry[];
  covert: CaseDropEntry[];
}

/**
 * A single entry in a case drop pool.
 */
export interface CaseDropEntry {
  /** References WeaponSkinDefinition.id */
  skinId: number;
  /** Relative weight within its rarity tier (higher = more likely) */
  weight: number;
}

/**
 * Result of opening a case (returned by server).
 */
export interface CaseOpenResult {
  /** The won skin definition ID */
  skinId: number;
  /** Generated wear float */
  wearFloat: number;
  /** Generated pattern seed (for pattern-paint skins) */
  patternSeed: number;
  /** Whether this is a StatTrak variant */
  isStatTrak: boolean;
  /** The rarity tier of the won skin */
  rarity: SkinRarity;
  /** The new inventory instance ID */
  instanceId: string;
  /** Display name of the won skin */
  skinName: string;
  /** Weapon this skin is for */
  weaponId: string;
}

/**
 * CS:GO-style rarity tier drop rates.
 * These are the base probabilities before the special item roll.
 *
 * The six weights must total exactly 1.0 so the rarity roll can't fall
 * into an undefined gap. The 0.00106 residual that used to be lost to
 * rounding is absorbed into the `consumer` tier (the most common drop)
 * so that every rarer tier keeps its published weight and `covert`
 * stays rarest.
 */
export const RARITY_DROP_RATES: Record<keyof CaseDropPool, number> = {
  consumer: 0.80026,
  industrial: 0.1598,
  'mil-spec': 0.0320,
  restricted: 0.0064,
  classified: 0.00128,
  covert: 0.00026,
};

/**
 * StatTrak chance when opening a case.
 */
export const STATTRAK_CHANCE = 0.10; // 10% chance

/**
 * Rarity display info for UI.
 */
export const RARITY_INFO: Record<SkinRarity, { label: string; color: string; glowColor: string }> = {
  consumer:    { label: 'Consumer Grade',   color: '#b0c3d9', glowColor: '#b0c3d944' },
  industrial:  { label: 'Industrial Grade', color: '#5e98d9', glowColor: '#5e98d944' },
  'mil-spec':  { label: 'Mil-Spec',        color: '#4b69ff', glowColor: '#4b69ff44' },
  restricted:  { label: 'Restricted',       color: '#8847ff', glowColor: '#8847ff44' },
  classified:  { label: 'Classified',       color: '#d32ce6', glowColor: '#d32ce644' },
  covert:      { label: 'Covert',           color: '#eb4b4b', glowColor: '#eb4b4b44' },
  contraband:  { label: 'Contraband',       color: '#e4ae39', glowColor: '#e4ae3944' },
  special:     { label: '\u2605 Rare Special',   color: '#e4ae39', glowColor: '#e4ae3988' },
};

/**
 * Unboxing animation phases.
 */
export type UnboxPhase =
  | 'idle'           // Case shown, waiting for open
  | 'opening'        // Key inserted, lid opening
  | 'scrolling'      // Reel scrolling through items
  | 'decelerating'   // Reel slowing down
  | 'reveal'         // Won item revealed with effects
  | 'complete';      // Animation done, showing result

// ────────────────────────────────────────────────────────────────────────
//  Weapon Cases
// ────────────────────────────────────────────────────────────────────────

/**
 * Starter weapon cases with populated drop pools.
 * Skin IDs reference entries in weapon-skin-manifest.ts and weapon-skin-manifest-knives.ts.
 */
export const WEAPON_CASES: WeaponCase[] = [
  // ── Arms Deal Case ──────────────────────────────────────────────────
  {
    id: 1,
    name: 'Arms Deal Case',
    description: 'The original weapon case. Contains classic skins from the early days.',
    keyPrice: 250,
    dropPool: {
      consumer: [
        { skinId: 1000, weight: 1 },  // AK-47 Safari Mesh
        { skinId: 2000, weight: 1 },  // M4A4 Urban DDPAT
        { skinId: 4000, weight: 1 },  // AWP Safari Mesh
        { skinId: 5000, weight: 1 },  // Deagle Urban DDPAT
        { skinId: 6000, weight: 1 },  // Glock Sand Dune
      ],
      industrial: [
        { skinId: 15001, weight: 1 }, // MP9 Rose Iron
      ],
      'mil-spec': [
        { skinId: 1001, weight: 1 },  // AK-47 Red Laminate
        { skinId: 15000, weight: 1 }, // MP9 Hydra
      ],
      restricted: [
        { skinId: 1002, weight: 1 },  // AK-47 Case Hardened
        { skinId: 6001, weight: 1 },  // Glock Water Elemental
        { skinId: 7002, weight: 1 },  // USP Cortex
      ],
      classified: [
        { skinId: 5001, weight: 1 },  // Deagle Blaze
        { skinId: 4001, weight: 1 },  // AWP Redline
      ],
      covert: [
        { skinId: 1004, weight: 1 },  // AK-47 Neon Revolution
      ],
    },
    specialPool: [
      { skinId: 20000, weight: 1 },   // Karambit Fade
      { skinId: 20010, weight: 1 },   // Butterfly Knife Fade
      { skinId: 20030, weight: 1 },   // Bayonet Doppler
    ],
    specialRate: 0.0026,
    active: true,
    collection: 'Arms Deal Collection',
  },

  // ── Phoenix Case ────────────────────────────────────────────────────
  {
    id: 2,
    name: 'Phoenix Case',
    description: 'Reborn from the ashes. Features fan-favorite designs.',
    keyPrice: 250,
    dropPool: {
      consumer: [
        { skinId: 6000, weight: 1 },  // Glock Sand Dune
        { skinId: 4000, weight: 1 },  // AWP Safari Mesh
      ],
      industrial: [
        { skinId: 15001, weight: 1 }, // MP9 Rose Iron
      ],
      'mil-spec': [
        { skinId: 15000, weight: 1 }, // MP9 Hydra
        { skinId: 12001, weight: 1 }, // P250 See Ya Later
      ],
      restricted: [
        { skinId: 2001, weight: 1 },  // M4A4 Dragon King
        { skinId: 17001, weight: 1 }, // P90 Death by Kitty
        { skinId: 18000, weight: 1 }, // Nova Hyper Beast
      ],
      classified: [
        { skinId: 1003, weight: 1 },  // AK-47 Redline
        { skinId: 2002, weight: 1 },  // M4A4 Asiimov
        { skinId: 17000, weight: 1 }, // P90 Asiimov
      ],
      covert: [
        { skinId: 7000, weight: 1 },  // USP Kill Confirmed
        { skinId: 4002, weight: 1 },  // AWP Dragon Lore
      ],
    },
    specialPool: [
      { skinId: 20001, weight: 1 },   // Karambit Doppler
      { skinId: 20020, weight: 1 },   // M9 Bayonet Tiger Tooth
      { skinId: 20040, weight: 1 },   // Flip Knife Autotronic
    ],
    specialRate: 0.0026,
    active: true,
    collection: 'Phoenix Collection',
  },

  // ── Chroma Case ─────────────────────────────────────────────────────
  {
    id: 3,
    name: 'Chroma Case',
    description: 'Vivid colors and bold patterns. Stand out on the battlefield.',
    keyPrice: 250,
    dropPool: {
      consumer: [
        { skinId: 1000, weight: 1 },  // AK-47 Safari Mesh
        { skinId: 5000, weight: 1 },  // Deagle Urban DDPAT
      ],
      industrial: [
        { skinId: 15001, weight: 1 }, // MP9 Rose Iron
      ],
      'mil-spec': [
        { skinId: 1001, weight: 1 },  // AK-47 Red Laminate
        { skinId: 13000, weight: 1 }, // Five-SeveN Monkey Business
      ],
      restricted: [
        { skinId: 2001, weight: 1 },  // M4A4 Dragon King
        { skinId: 12001, weight: 1 }, // P250 See Ya Later
      ],
      classified: [
        { skinId: 12000, weight: 1 }, // P250 Mehndi
        { skinId: 7001, weight: 1 },  // USP Neo-Noir
        { skinId: 19001, weight: 1 }, // SSG 08 Dragonfire
      ],
      covert: [
        { skinId: 13001, weight: 1 }, // Five-SeveN Hyper Beast
        { skinId: 19000, weight: 1 }, // SSG 08 Blood in the Water
      ],
    },
    specialPool: [
      { skinId: 20002, weight: 1 },   // Karambit Crimson Web
      { skinId: 20011, weight: 1 },   // Butterfly Knife Marble Fade
      { skinId: 20021, weight: 1 },   // M9 Bayonet Slaughter
      { skinId: 20050, weight: 1 },   // Skeleton Knife Crimson Web
      { skinId: 20060, weight: 1 },   // Talon Knife Case Hardened
    ],
    specialRate: 0.0026,
    active: true,
    collection: 'Chroma Collection',
  },
];
